import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Button,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { FaSearch } from 'react-icons/fa';
import {
  uploadVideo,
  createVideoLesson,
  getMyVideoLessons,
  updateVideoLesson,
  deleteVideoLesson,
} from '../api/videoLessonApi';
import { getApiErrorMessage } from '../utils/apiError';
import SignedVideoPlayer from './SignedVideoPlayer';
import '../styles/VideoLessons.css';

const VideoLessons = () => {
  const navigate = useNavigate();

  const handleBackClick = () => {
    navigate('/dashboard');
  };

  const [activeTab, setActiveTab] = useState('manage');
  const [videoLessons, setVideoLessons] = useState([]);
  const [filteredLessons, setFilteredLessons] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedThumbnail, setSelectedThumbnail] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    videoUrl: '',
  });
  const [editingLesson, setEditingLesson] = useState(null);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [notification, setNotification] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});
  const fileInputRef = useRef(null);
  const thumbnailInputRef = useRef(null);
  const pollingIntervalRef = useRef(null);
  const previousLessonsRef = useRef([]);

  useEffect(() => {
    if (activeTab === 'manage') {
      fetchMyVideoLessons(currentPage, searchTerm);
    } else {
      stopPolling();
    }

    return () => {
      stopPolling();
    };
  }, [activeTab, currentPage]);

  useEffect(() => {
    if (activeTab === 'manage' && hasProcessingVideos(videoLessons)) {
      startPolling();
    } else {
      stopPolling();
    }
  }, [videoLessons, activeTab]);

  const startPolling = () => {
    if (pollingIntervalRef.current) {
      return;
    }

    pollingIntervalRef.current = setInterval(() => {
      checkProcessingVideos();
    }, 5000);
  };

  const stopPolling = () => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
  };

  const hasProcessingVideos = (lessons) => {
    return lessons.some((lesson) => lesson.processingStatus === 'processing');
  };

  const checkProcessingVideos = async () => {
    try {
      const response = await getMyVideoLessons(currentPage, 9, searchTerm);
      const updatedLessons = response?.data || response || [];
      const lessonArray = Array.isArray(updatedLessons)
        ? updatedLessons
        : updatedLessons.videoLessons || updatedLessons.lessons || updatedLessons.data || [];

      checkForCompletedVideos(previousLessonsRef.current, lessonArray);

      previousLessonsRef.current = lessonArray;
      setVideoLessons(lessonArray);
      setFilteredLessons(lessonArray);
    } catch (error) {
      console.error('Error checking processing videos:', error);
    }
  };

  const checkForCompletedVideos = (previousLessons, currentLessons) => {
    previousLessons.forEach((prevLesson) => {
      const currentLesson = currentLessons.find((l) => l.id === prevLesson.id);

      if (
        prevLesson.processingStatus === 'processing' &&
        currentLesson?.processingStatus === 'completed'
      ) {
        showNotification(`"${currentLesson.title}" is now ready to play!`, 'success');
      }
    });
  };

  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => {
      setNotification(null);
    }, 5000);
  };

  const fetchMyVideoLessons = async (page = currentPage, query = searchTerm) => {
    try {
      setLoading(true);
      const response = await getMyVideoLessons(page, 9, query);
      let lessons = [];
      let totalP = 1;

      if (Array.isArray(response)) {
        lessons = response;
        totalP = Math.ceil(response.length / 9) || 1;
      } else if (response?.data) {
        if (Array.isArray(response.data)) {
          lessons = response.data;
          const totalL = response.total || response.totalLessons || response.data.length;
          totalP = response.totalPages || response.pagination?.last_page || Math.ceil(totalL / 9) || 1;
        } else if (Array.isArray(response.data.videoLessons)) {
          lessons = response.data.videoLessons;
          const totalL = response.data.total || response.data.totalLessons || lessons.length;
          totalP = response.data.totalPages || Math.ceil(totalL / 9) || 1;
        } else if (Array.isArray(response.data.lessons)) {
          lessons = response.data.lessons;
          const totalL = response.data.total || response.data.totalLessons || lessons.length;
          totalP = response.data.totalPages || Math.ceil(totalL / 9) || 1;
        } else if (Array.isArray(response.data.data)) {
          lessons = response.data.data;
          const totalL = response.data.total || lessons.length;
          totalP = response.data.last_page || response.data.totalPages || Math.ceil(totalL / 9) || 1;
        }
      }

      previousLessonsRef.current = lessons;
      setVideoLessons(lessons);
      setFilteredLessons(lessons);
      setTotalPages(totalP);
    } catch (error) {
      console.error('Error fetching video lessons:', error);
      setVideoLessons([]);
      setFilteredLessons([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e?.preventDefault();
    setCurrentPage(1);
    fetchMyVideoLessons(1, searchTerm);
  };

  const handleClearSearch = () => {
    setSearchTerm('');
    setCurrentPage(1);
    fetchMyVideoLessons(1, '');
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
      fetchMyVideoLessons(newPage, searchTerm);
    }
  };

  const handleVideoClick = (video) => {
    setSelectedVideo(video);
  };

  const handleCloseVideo = () => {
    setSelectedVideo(null);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    // Enforce character limits
    if (name === 'title') {
      if (value.length > 150) {
        setValidationErrors((prev) => ({
          ...prev,
          title: 'Title cannot exceed 150 characters',
        }));
        return;
      }
    } else if (name === 'description') {
      if (value.length > 250) {
        setValidationErrors((prev) => ({
          ...prev,
          description: 'Description cannot exceed 250 characters',
        }));
        return;
      }
    }

    setValidationErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors[name];
      return newErrors;
    });

    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 500 * 1024 * 1024) {
        alert('File size must be less than 500MB');
        return;
      }

      if (!file.type.startsWith('video/')) {
        alert('Please select a video file');
        return;
      }

      setSelectedFile(file);
    }
  };

  const handleThumbnailSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        alert('Thumbnail size must be less than 5MB');
        return;
      }

      if (!file.type.startsWith('image/')) {
        alert('Please select an image file');
        return;
      }

      setSelectedThumbnail(file);
    }
  };

  const handleRemoveThumbnail = () => {
    setSelectedThumbnail(null);
    if (thumbnailInputRef.current) {
      thumbnailInputRef.current.value = '';
    }
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const formatDuration = (seconds) => {
    if (!seconds) return 'N/A';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;

    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  };

  const handleCreateVideoLesson = async (e) => {
    e.preventDefault();

    if (!formData.title) {
      alert('Please provide a title');
      return;
    }

    if (!selectedFile) {
      alert('Please select a video to upload');
      return;
    }

    try {
      setUploading(true);
      setUploadProgress(0);

      await createVideoLesson(
        {
          title: formData.title,
          description: formData.description,
        },
        selectedFile,
        selectedThumbnail,
        (progress) => {
          setUploadProgress(progress);
        }
      );

      // Reset form and switch to manage tab first
      setFormData({ title: '', description: '', videoUrl: '' });
      setSelectedFile(null);
      setSelectedThumbnail(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      if (thumbnailInputRef.current) {
        thumbnailInputRef.current.value = '';
      }
      setActiveTab('manage');
      setCurrentPage(1);

      // Fetch new lessons immediately
      await fetchMyVideoLessons(1, '');

      // Show success alert after UI has updated (1.5 seconds delay)
      setTimeout(() => {
        alert('Video lesson created successfully! Video is being processed and will be available shortly.');
      }, 1500);
    } catch (error) {
      console.error('Error creating video lesson:', error);
      alert(getApiErrorMessage(error, 'Failed to create video lesson. Please try again.'));
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleEditLesson = (lesson) => {
    setEditingLesson({
      id: lesson.id,
      title: lesson.title || '',
      description: lesson.description || '',
    });
  };

  const handleUpdateLesson = async () => {
    if (!editingLesson?.title?.trim()) {
      alert('Title is required');
      return;
    }

    try {
      setLoading(true);
      await updateVideoLesson(editingLesson.id, {
        title: editingLesson.title.trim(),
        description: editingLesson.description.trim(),
      });
      alert('Video lesson updated successfully!');
      setEditingLesson(null);
      fetchMyVideoLessons(currentPage, searchTerm);
    } catch (error) {
      console.error('Error updating video lesson:', error);
      alert(getApiErrorMessage(error, 'Failed to update video lesson. Please try again.'));
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteLesson = async (id) => {
    if (!window.confirm('Are you sure you want to delete this video lesson?')) {
      return;
    }

    try {
      setLoading(true);
      await deleteVideoLesson(id);
      alert('Video lesson deleted successfully!');
      fetchMyVideoLessons(currentPage, searchTerm);
    } catch (error) {
      console.error('Error deleting video lesson:', error);
      alert(getApiErrorMessage(error, 'Failed to delete video lesson. Please try again.'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="video-lessons-container">
      {notification && (
        <div className={`notification ${notification.type}`}>
          <span>{notification.message}</span>
          <button onClick={() => setNotification(null)} className="notification-close">
            ×
          </button>
        </div>
      )}

      {/* Page Header: Title centered, Back button below aligned to left */}
      <div style={{ marginBottom: '25px' }}>
        <h1
          style={{
            fontSize: '32px',
            color: '#333',
            margin: '0 0 16px 0',
            textAlign: 'center',
          }}
        >
          Video Lessons
        </h1>
        <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
          <Button
            variant="contained"
            color="secondary"
            onClick={handleBackClick}
          >
            Back to Dashboard
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <div className="tabs">
        <button
          className={`tab ${activeTab === 'create' ? 'active' : ''}`}
          onClick={() => setActiveTab('create')}
        >
          Upload Lesson
        </button>
        <button
          className={`tab ${activeTab === 'manage' ? 'active' : ''}`}
          onClick={() => setActiveTab('manage')}
        >
          Uploaded Lessons
        </button>
      </div>

      {activeTab === 'create' && (
        <div className="create-lesson-container">
          <form onSubmit={handleCreateVideoLesson} className="lesson-form">
            <div className="form-group">
              <label htmlFor="title">Title *</label>
              <input
                type="text"
                id="title"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                placeholder="Enter lesson title"
                maxLength="150"
                required
              />
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginTop: '4px',
                }}
              >
                <span
                  style={{
                    fontSize: '12px',
                    color: validationErrors.title ? '#d32f2f' : '#666',
                  }}
                >
                  {validationErrors.title || ''}
                </span>
                <span
                  style={{
                    fontSize: '12px',
                    color: formData.title.length >= 150 ? '#d32f2f' : '#999',
                  }}
                >
                  {formData.title.length}/150
                </span>
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="description">Description</label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                placeholder="Enter lesson description"
                rows="4"
                maxLength="250"
              />
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginTop: '4px',
                }}
              >
                <span
                  style={{
                    fontSize: '12px',
                    color: validationErrors.description ? '#d32f2f' : '#666',
                  }}
                >
                  {validationErrors.description || ''}
                </span>
                <span
                  style={{
                    fontSize: '12px',
                    color: formData.description.length >= 250 ? '#d32f2f' : '#999',
                  }}
                >
                  {formData.description.length}/250
                </span>
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="videoFile">Upload Video *</label>
              <div className="file-upload-container">
                <input
                  type="file"
                  id="videoFile"
                  ref={fileInputRef}
                  accept="video/*"
                  onChange={handleFileSelect}
                  className="file-input"
                />
                {selectedFile && (
                  <div className="file-info">
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        marginBottom: '4px',
                      }}
                    >
                      <p className="file-name" style={{ margin: 0 }}>
                        Video: {selectedFile.name}
                      </p>
                      <button
                        type="button"
                        onClick={handleRemoveFile}
                        title="Remove Video"
                        style={{
                          background: '#ff4757',
                          color: '#fff',
                          border: 'none',
                          borderRadius: '50%',
                          width: '24px',
                          height: '24px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: 'pointer',
                          fontSize: '16px',
                          fontWeight: 'bold',
                          lineHeight: 1,
                          padding: 0,
                        }}
                      >
                        ×
                      </button>
                    </div>
                    <p className="file-size">
                      Size: {(selectedFile.size / (1024 * 1024)).toFixed(2)} MB
                    </p>
                  </div>
                )}
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="thumbnailFile">Upload Thumbnail (Optional)</label>
              <div className="file-upload-container">
                <input
                  type="file"
                  id="thumbnailFile"
                  ref={thumbnailInputRef}
                  accept="image/*"
                  onChange={handleThumbnailSelect}
                  className="file-input"
                />
                {selectedThumbnail && (
                  <div className="file-info">
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        marginBottom: '4px',
                      }}
                    >
                      <p className="file-name" style={{ margin: 0 }}>
                        Thumbnail: {selectedThumbnail.name}
                      </p>
                      <button
                        type="button"
                        onClick={handleRemoveThumbnail}
                        title="Remove Thumbnail"
                        style={{
                          background: '#ff4757',
                          color: '#fff',
                          border: 'none',
                          borderRadius: '50%',
                          width: '24px',
                          height: '24px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: 'pointer',
                          fontSize: '16px',
                          fontWeight: 'bold',
                          lineHeight: 1,
                          padding: 0,
                        }}
                      >
                        ×
                      </button>
                    </div>
                    <p className="file-size">
                      Size: {(selectedThumbnail.size / (1024 * 1024)).toFixed(2)} MB
                    </p>
                    <div style={{ position: 'relative', display: 'inline-block', marginTop: '10px' }}>
                      <img
                        src={URL.createObjectURL(selectedThumbnail)}
                        alt="Thumbnail preview"
                        style={{ maxWidth: '200px', maxHeight: '150px', borderRadius: '8px', display: 'block' }}
                      />
                      <button
                        type="button"
                        onClick={handleRemoveThumbnail}
                        title="Remove Thumbnail"
                        style={{
                          position: 'absolute',
                          top: '6px',
                          right: '6px',
                          backgroundColor: 'rgba(0, 0, 0, 0.7)',
                          color: '#fff',
                          border: 'none',
                          borderRadius: '50%',
                          width: '28px',
                          height: '28px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: 'pointer',
                          fontSize: '18px',
                          fontWeight: 'bold',
                          lineHeight: 1,
                          boxShadow: '0 2px 6px rgba(0, 0, 0, 0.3)',
                          transition: 'background-color 0.2s',
                        }}
                      >
                        ×
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {uploading && (
              <div className="upload-progress">
                <div className="progress-bar">
                  <div
                    className="progress-fill"
                    style={{ width: `${uploadProgress}%` }}
                  />
                </div>
                <p className="progress-text">Uploading: {uploadProgress}%</p>
              </div>
            )}

            <button
              type="submit"
              className="submit-button"
              disabled={uploading || !selectedFile}
            >
              {uploading ? `Uploading... ${uploadProgress}%` : 'Create Lesson'}
            </button>
          </form>
        </div>
      )}

      {activeTab === 'manage' && (
        <div className="manage-lessons-container">
          {hasProcessingVideos(filteredLessons) && (
            <div className="processing-banner">
              <div className="processing-banner-spinner"></div>
              <span>Videos are being processed. This page will update automatically when they're ready.</span>
            </div>
          )}

          {/* Search container with search icon */}
          <div className="search-container">
            <form onSubmit={handleSearch} className="search-form">
              <div className="search-input-wrapper">
                <span className="search-input-icon">
                  <FaSearch size={16} />
                </span>
                <input
                  type="text"
                  placeholder="Search your video lessons..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="search-input"
                />
              </div>
              <button type="submit" className="search-button">
                Search
              </button>
              {searchTerm && (
                <button
                  type="button"
                  onClick={handleClearSearch}
                  className="search-button"
                  style={{ backgroundColor: '#6c757d' }}
                >
                  Clear
                </button>
              )}
            </form>
          </div>

          {loading ? (
            <div className="loading">Loading...</div>
          ) : filteredLessons.length === 0 ? (
            <div className="no-lessons">
              <p>
                {searchTerm
                  ? 'No lessons found matching your search.'
                  : 'No video lessons yet. Create your first lesson!'}
              </p>
            </div>
          ) : (
            <>
              {/* 3x3 Grid */}
              <div className="lessons-grid">
                {filteredLessons.map((lesson) => (
                  <div key={lesson.id} className="lesson-card">
                    {/* Date aligned to top-right corner of the card */}
                    <div className="lesson-date-badge">
                      {lesson.createdAt ? new Date(lesson.createdAt).toLocaleDateString() : ''}
                    </div>

                    <div
                      className="video-thumbnail"
                      onClick={() =>
                        lesson.processingStatus === 'completed' ? handleVideoClick(lesson) : null
                      }
                      style={{
                        cursor: lesson.processingStatus === 'completed' ? 'pointer' : 'default',
                      }}
                    >
                      {lesson.processingStatus === 'processing' || !lesson.videoUrl ? (
                        <div className="processing-placeholder">
                          <div className="processing-spinner"></div>
                          <p>Processing...</p>
                        </div>
                      ) : lesson.processingStatus === 'failed' ? (
                        <div className="failed-placeholder">
                          <p>Upload Failed</p>
                        </div>
                      ) : (
                        <>
                          {lesson.thumbnailUrl ? (
                            <img
                              src={lesson.thumbnailUrl}
                              alt={lesson.title}
                              className="video-thumbnail-image"
                            />
                          ) : (
                            <div className="video-thumbnail-placeholder">
                              <svg
                                viewBox="0 0 24 24"
                                fill="currentColor"
                                xmlns="http://www.w3.org/2000/svg"
                                style={{ width: '60px', height: '60px', opacity: 0.3 }}
                              >
                                <path d="M17 10.5V7c0-.55-.45-1-1-1H4c-.55 0-1 .45-1 1v10c0 .55.45 1 1 1h12c.55 0 1-.45 1-1v-3.5l4 4v-11l-4 4z" />
                              </svg>
                            </div>
                          )}
                          {lesson.processingStatus === 'completed' && lesson.duration && (
                            <div className="video-duration-badge">
                              {formatDuration(lesson.duration)}
                            </div>
                          )}
                          <div className="play-overlay">
                            <div className="play-button">
                              <svg
                                viewBox="0 0 24 24"
                                fill="currentColor"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path d="M8 5v14l11-7z" />
                              </svg>
                            </div>
                          </div>
                        </>
                      )}
                    </div>

                    <div className="lesson-info" style={{ padding: '16px 20px', display: 'flex', flexDirection: 'column', flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '2px' }}>
                        <Tooltip title={lesson.title || ''} arrow>
                          <h3
                            style={{
                              fontSize: '1.15rem',
                              fontWeight: 700,
                              color: '#333',
                              margin: 0,
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              flex: 1,
                              minWidth: 0,
                            }}
                          >
                            {lesson.title}
                          </h3>
                        </Tooltip>
                        {lesson.processingStatus === 'processing' && (
                          <span className="status-badge processing" style={{ flexShrink: 0 }}>
                            Processing
                          </span>
                        )}
                        {lesson.processingStatus === 'failed' && (
                          <span className="status-badge failed" style={{ flexShrink: 0 }}>
                            Failed
                          </span>
                        )}
                      </div>

                      {/* Reduced vertical spacing between title and description */}
                      <Tooltip title={lesson.description || 'No description'} arrow>
                        <p
                          style={{
                            fontSize: '0.9rem',
                            color: '#666',
                            margin: '2px 0 8px 0',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            display: 'block',
                            width: '100%',
                          }}
                        >
                          {lesson.description || 'No description'}
                        </p>
                      </Tooltip>

                      <div
                        className="lesson-meta"
                        style={{
                          marginTop: 'auto',
                          marginBottom: '8px',
                          display: 'flex',
                          gap: '15px',
                          color: '#999',
                          fontSize: '0.85rem',
                        }}
                      >
                        <span>{lesson.viewCount || 0} views</span>
                      </div>

                      <div className="lesson-actions" style={{ display: 'flex', gap: '10px', marginTop: '4px' }}>
                        <button
                          onClick={() => handleEditLesson(lesson)}
                          className="edit-button"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDeleteLesson(lesson.id)}
                          className="delete-button"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Standard Pagination UI */}
              {!loading && filteredLessons.length > 0 && totalPages > 1 && (
                <div
                  style={{
                    marginTop: '30px',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                >
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={loading || currentPage === 1}
                    style={{
                      padding: '8px 16px',
                      marginRight: '10px',
                      border: '1px solid #ccc',
                      borderRadius: '4px',
                      backgroundColor: currentPage === 1 ? '#e9ecef' : '#fff',
                      cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                    }}
                  >
                    Previous
                  </button>
                  <span style={{ margin: '0 10px', fontSize: '14px', color: '#555' }}>
                    Page {currentPage} of {totalPages}
                  </span>
                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={loading || currentPage === totalPages}
                    style={{
                      padding: '8px 16px',
                      marginLeft: '10px',
                      border: '1px solid #ccc',
                      borderRadius: '4px',
                      backgroundColor: currentPage === totalPages ? '#e9ecef' : '#fff',
                      cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                    }}
                  >
                    Next
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Edit Lesson Modal */}
      <Dialog
        open={Boolean(editingLesson)}
        onClose={() => setEditingLesson(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 'bold', fontSize: '20px' }}>
          Edit Lesson
        </DialogTitle>
        <DialogContent dividers>
          <div style={{ marginBottom: '20px' }}>
            <label
              style={{
                display: 'block',
                marginBottom: '8px',
                fontWeight: 600,
                color: '#333',
              }}
            >
              Title *
            </label>
            <input
              type="text"
              value={editingLesson?.title || ''}
              onChange={(e) => {
                const newTitle = e.target.value;
                if (newTitle.length > 150) return;
                setEditingLesson((prev) => ({ ...prev, title: newTitle }));
              }}
              placeholder="Enter lesson title"
              maxLength="150"
              required
              style={{
                width: '100%',
                padding: '12px',
                border: '2px solid #e0e0e0',
                borderRadius: '8px',
                fontSize: '1rem',
                boxSizing: 'border-box',
              }}
            />
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginTop: '4px',
              }}
            >
              <span style={{ fontSize: '12px', color: '#d32f2f' }}>
                {!editingLesson?.title?.trim() ? 'Title is required' : ''}
              </span>
              <span
                style={{
                  fontSize: '12px',
                  color: (editingLesson?.title?.length || 0) >= 150 ? '#d32f2f' : '#999',
                }}
              >
                {editingLesson?.title?.length || 0}/150
              </span>
            </div>
          </div>

          <div>
            <label
              style={{
                display: 'block',
                marginBottom: '8px',
                fontWeight: 600,
                color: '#333',
              }}
            >
              Description
            </label>
            <textarea
              value={editingLesson?.description || ''}
              onChange={(e) => {
                const newDesc = e.target.value;
                if (newDesc.length > 250) return;
                setEditingLesson((prev) => ({ ...prev, description: newDesc }));
              }}
              placeholder="Enter lesson description"
              rows="4"
              maxLength="250"
              style={{
                width: '100%',
                padding: '12px',
                border: '2px solid #e0e0e0',
                borderRadius: '8px',
                fontSize: '1rem',
                boxSizing: 'border-box',
                resize: 'vertical',
              }}
            />
            <div
              style={{
                display: 'flex',
                justifyContent: 'flex-end',
                marginTop: '4px',
              }}
            >
              <span
                style={{
                  fontSize: '12px',
                  color: (editingLesson?.description?.length || 0) >= 250 ? '#d32f2f' : '#999',
                }}
              >
                {editingLesson?.description?.length || 0}/250
              </span>
            </div>
          </div>
        </DialogContent>
        <DialogActions sx={{ padding: '16px 24px' }}>
          <Button
            onClick={() => setEditingLesson(null)}
            variant="outlined"
            color="inherit"
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleUpdateLesson}
            variant="contained"
            color="primary"
            disabled={loading || !editingLesson?.title?.trim()}
          >
            {loading ? 'Updating...' : 'Update & Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Video Modal */}
      {selectedVideo && (
        <div className="video-modal" onClick={handleCloseVideo}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="close-button" onClick={handleCloseVideo}>
              ✕
            </button>
            <div className="modal-video">
              <SignedVideoPlayer
                videoUrl={selectedVideo.videoUrl}
                className="video-player-modal"
                controls
                autoPlay
              />
            </div>
            <div className="modal-info">
              <h2>{selectedVideo.title}</h2>
              <p className="modal-description">{selectedVideo.description}</p>
              <div className="modal-stats">
                <span>{selectedVideo.viewCount || 0} views</span>
                <span>{formatDuration(selectedVideo.duration)}</span>
                <span>{new Date(selectedVideo.createdAt).toLocaleDateString()}</span>
              </div>
              <div className="modal-actions">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleCloseVideo();
                    handleEditLesson(selectedVideo);
                  }}
                  className="modal-edit-button"
                >
                  Edit Lesson
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleCloseVideo();
                    handleDeleteLesson(selectedVideo.id);
                  }}
                  className="modal-delete-button"
                >
                  Delete Lesson
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VideoLessons;

